import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar,
  Search,
  Loader,
  ChevronDown,
  ChevronUp,
  History,
  Clock,
  TrendingDown,
  MinusCircle,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';

const DeductionCalendar = ({
  staff,
  loading,
  fetchDeductionData,
  setError,
  setSuccess
}) => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken');
  };

  // State management - simplified to match backend
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [calendarDeductions, setCalendarDeductions] = useState({});
  const [selectedStaffFilter, setSelectedStaffFilter] = useState('');
  const [customCalendarLoading, setCustomCalendarLoading] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});

  // Get current month in YYYY-MM format
  const getCurrentPeriod = () => {
    const now = new Date();
    const lagosNow = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Lagos" }));
    return `${lagosNow.getFullYear()}-${String(lagosNow.getMonth() + 1).padStart(2, '0')}`;
  };

  // Load available periods from backend
  const loadAvailablePeriods = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/payroll/available-periods`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailablePeriods(data.periods || []);
        
        // Auto-select current period if available
        const currentPeriod = getCurrentPeriod();
        if (data.periods.includes(currentPeriod)) {
          setSelectedPeriod(currentPeriod);
        } else if (data.periods.length > 0) {
          // Select most recent period available
          setSelectedPeriod(data.periods[0]);
        }
        
        console.log('Available periods loaded:', data.periods);
      }
    } catch (err) {
      console.error('Error loading available periods:', err);
      setError('Failed to load available periods');
    }
  }, [setError]);

  // Load calendar data - bow to backend completely
  const loadCalendarData = useCallback(async () => {
    if (!selectedPeriod || customCalendarLoading) return;
    
    try {
      setCustomCalendarLoading(true);
      
      // SIMPLE: Just send period to backend, let it handle everything
      const queryParams = new URLSearchParams({
        period: selectedPeriod
      });

      if (selectedStaffFilter) {
        queryParams.append('employeeId', selectedStaffFilter);
      }

      console.log('Loading calendar data for period:', selectedPeriod);

      const response = await fetchDeductionData(Object.fromEntries(queryParams));

      if (response && response.deductionsData) {
        // Group by date
        const grouped = {};
        response.deductionsData.forEach(record => {
          const date = record.date;
          if (!grouped[date]) grouped[date] = [];
          grouped[date].push(record);
        });

        setCalendarDeductions(grouped);
        console.log(`Loaded ${Object.keys(grouped).length} days with deductions for ${selectedPeriod}`);
      } else {
        setCalendarDeductions({});
        console.log(`No deduction data found for ${selectedPeriod}`);
      }

    } catch (err) {
      console.error('Error loading calendar data:', err);
      if (err.message && err.message.includes('404')) {
        setError(`No data available for ${selectedPeriod}. Try running historical recalculation.`);
      } else {
        setError('Failed to load calendar data');
      }
      setCalendarDeductions({});
    } finally {
      setCustomCalendarLoading(false);
    }
  }, [selectedPeriod, selectedStaffFilter, fetchDeductionData, setError]);

  // Load available periods on mount
  useEffect(() => {
    loadAvailablePeriods();
  }, [loadAvailablePeriods]);

  // Load data when period changes
  useEffect(() => {
    if (selectedPeriod) {
      loadCalendarData();
    }
  }, [selectedPeriod, selectedStaffFilter, loadCalendarData]);

  // Toggle date expansion
  const toggleDateExpansion = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Format currency
  const formatCurrency = (amount) => `₦${Math.round(amount || 0).toLocaleString('en-NG')}`;

  // Format period for display
  const formatPeriodDisplay = (period) => {
    if (!period) return '';
    const [year, month] = period.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 flex items-center gap-2">
          <Calendar className="text-blue-600" size={24} />
          Deduction Calendar
        </h2>

        {/* Simplified Period Selection */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <History className="text-blue-600" size={20} />
            Select Calendar Period
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Period Selection - Bow to Backend */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline mr-1" size={16} />
                Available Periods
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                disabled={availablePeriods.length === 0}
              >
                <option value="">Select period...</option>
                {availablePeriods.map(period => (
                  <option key={period} value={period}>
                    {formatPeriodDisplay(period)} ({period})
                  </option>
                ))}
              </select>
              {availablePeriods.length === 0 && (
                <p className="text-xs text-red-500 mt-1">No periods available. Run historical recalculation.</p>
              )}
            </div>

            {/* Staff Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Staff</label>
              <select
                value={selectedStaffFilter}
                onChange={(e) => setSelectedStaffFilter(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="">All Staff</option>
                {staff.map(staffMember => (
                  <option key={staffMember._id} value={staffMember.employeeId}>
                    {staffMember.firstName} {staffMember.lastName} ({staffMember.employeeId})
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  loadAvailablePeriods();
                  if (selectedPeriod) loadCalendarData();
                }}
                disabled={customCalendarLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {customCalendarLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Period Info */}
          {selectedPeriod && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Selected Period:</span>
                <span className="font-semibold text-blue-700">
                  {formatPeriodDisplay(selectedPeriod)} ({selectedPeriod})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {customCalendarLoading && (
          <div className="flex items-center justify-center gap-3 p-4 py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading deduction calendar for {selectedPeriod}...</span>
          </div>
        )}

        {/* No Period Selected */}
        {!selectedPeriod && !customCalendarLoading && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">Select a period to view calendar</p>
            <p className="text-gray-400">Choose from available periods above</p>
          </div>
        )}

        {/* No Data Found */}
        {!customCalendarLoading && selectedPeriod && Object.keys(calendarDeductions).length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No deductions found for {formatPeriodDisplay(selectedPeriod)}</p>
            <p className="text-gray-400">This period may have no deduction records</p>
          </div>
        )}

        {/* Calendar Display */}
        {!customCalendarLoading && Object.keys(calendarDeductions).length > 0 && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Days with Deductions</p>
                    <p className="text-2xl font-bold text-blue-600">{Object.keys(calendarDeductions).length}</p>
                  </div>
                  <Calendar className="text-blue-500" size={32} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-800">Total Amount</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(
                        Object.values(calendarDeductions)
                          .flat()
                          .reduce((sum, record) => sum + record.totalDeduction, 0)
                      )}
                    </p>
                  </div>
                  <MinusCircle className="text-red-500" size={32} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800">Staff Affected</p>
                    <p className="text-2xl font-bold text-green-600">
                      {new Set(
                        Object.values(calendarDeductions)
                          .flat()
                          .map(record => record.employeeId)
                      ).size}
                    </p>
                  </div>
                  <AlertCircle className="text-green-500" size={32} />
                </div>
              </div>
            </div>

            {/* Calendar Days List */}
            {Object.entries(calendarDeductions)
              .filter(([date, records]) => {
                // Apply staff filter if selected
                if (selectedStaffFilter) {
                  return records.some(record => record.employeeId === selectedStaffFilter);
                }
                return true;
              })
              .sort(([a], [b]) => b.localeCompare(a)) // Sort by date descending
              .map(([date, records]) => {
                // Filter records by staff if selected
                const filteredRecords = selectedStaffFilter 
                  ? records.filter(record => record.employeeId === selectedStaffFilter)
                  : records;
                
                const isExpanded = expandedDates[date];
                const totalDeductionsForDate = filteredRecords.reduce((sum, record) => sum + record.totalDeduction, 0);
                const dateObj = new Date(date + 'T12:00:00');
                const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Africa/Lagos' });
                const formattedDate = dateObj.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  timeZone: 'Africa/Lagos'
                });

                return (
                  <div key={date} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <button
                      onClick={() => toggleDateExpansion(date)}
                      className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">{formattedDate}</h3>
                          <p className="text-sm text-gray-600">{dayName}</p>
                        </div>
                        <div className="bg-white px-3 py-1 rounded-full border shadow-sm">
                          <span className="text-sm font-medium text-gray-700">
                            {filteredRecords.length} employee{filteredRecords.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">{formatCurrency(totalDeductionsForDate)}</p>
                          <p className="text-sm text-gray-500">Total Deductions</p>
                        </div>
                        <div className="bg-white p-2 rounded-full shadow-sm">
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="divide-y divide-gray-200">
                        {filteredRecords.map((record, index) => (
                          <div key={index} className="p-4 bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {record.employeeName} ({record.employeeId})
                                </h4>
                                <p className="text-sm text-gray-600">{record.position} • {record.department}</p>
                                <p className="text-sm text-green-600 font-medium">
                                  Base Salary: {formatCurrency(record.baseSalary)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-red-600">{formatCurrency(record.totalDeduction)}</p>
                                <p className="text-sm text-gray-500">Total Deducted</p>
                              </div>
                            </div>

                            {record.deductions && record.deductions.length > 0 && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {record.deductions.map((deduction, i) => {
                                  const getDeductionColor = (type) => {
                                    switch (type.toLowerCase()) {
                                      case 'lateness': return 'bg-orange-50 border-orange-200 text-orange-800';
                                      case 'early leave': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
                                      case 'absence': return 'bg-red-50 border-red-200 text-red-800';
                                      default: return 'bg-gray-50 border-gray-200 text-gray-800';
                                    }
                                  };

                                  const getDeductionIcon = (type) => {
                                    switch (type.toLowerCase()) {
                                      case 'lateness': return <Clock size={16} />;
                                      case 'early leave': return <TrendingDown size={16} />;
                                      case 'absence': return <MinusCircle size={16} />;
                                      default: return <AlertCircle size={16} />;
                                    }
                                  };

                                  return (
                                    <div key={i} className={`p-3 rounded-lg border-2 ${getDeductionColor(deduction.type)}`}>
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          {getDeductionIcon(deduction.type)}
                                          <span className="text-sm font-bold">{deduction.type}</span>
                                        </div>
                                        <span className="text-lg font-bold">{formatCurrency(deduction.amount)}</span>
                                      </div>
                                      {deduction.minutes && (
                                        <p className="text-xs font-medium opacity-75 mb-1">
                                          Duration: {deduction.minutes} minutes
                                        </p>
                                      )}
                                      {deduction.description && (
                                        <p className="text-xs opacity-75 leading-relaxed">
                                          {deduction.description}
                                        </p>
                                      )}
                                      {deduction.calculationMethod && (
                                        <p className="text-xs text-blue-600 mt-1 font-medium">
                                          Method: {deduction.calculationMethod}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeductionCalendar;